/**
 * MarketplacePage - Browse and discover plugin presets
 */

import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import NotificationCenter from '../components/NotificationCenter';
import {
  Search,
  Filter,
  Star,
  ArrowUp,
  Download,
  Music,
  Sliders,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Clock,
  User,
  X,
  Plus,
  Grid,
  List,
  Heart,
  BarChart3,
  ShoppingCart,
  DollarSign,
  Package,
  Shield,
  Headphones,
  Play,
  Pause,
  Volume2,
  Guitar,
} from 'lucide-react';
import { PresetPreviewButton } from '../components/marketplace/PresetPreviewButton';

const ReverbGearBrowser = lazy(() => import('../components/marketplace/ReverbGearBrowser'));

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin emails - authorized users for admin panel
const ADMIN_EMAILS = [
  'turnersouthsyndicate@gmail.com',
  // Add more admin emails as needed
];

// Preset Card Component
const PresetCard = ({ preset, onDownload, onUpvote, onViewDetails, onPurchase, currentUserId }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  
  // Price display helper
  const getPrice = () => {
    const priceTier = preset.price_tier || 'free';
    const prices = { free: 0, basic: 2.99, standard: 4.99, premium: 9.99, professional: 19.99 };
    return prices[priceTier] || 0;
  };
  
  const price = getPrice();
  const isFree = price === 0;
  const isOwner = preset.author_id === currentUserId;
  const isPurchased = preset.is_purchased || false;
  
  const handlePurchase = async (e) => {
    e.stopPropagation();
    if (isFree || isOwner || isPurchased) {
      onDownload(preset.id);
      return;
    }
    setIsPurchasing(true);
    try {
      await onPurchase(preset.id);
    } finally {
      setIsPurchasing(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden cursor-pointer group"
      onClick={() => onViewDetails(preset)}
      data-testid={`preset-card-${preset.id}`}
    >
      {/* Header with type badge */}
      <div className="relative h-24 bg-gradient-to-br from-violet-600/20 to-rose-600/20 flex items-center justify-center">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
          preset.preset_type === 'master' 
            ? 'bg-gradient-to-br from-amber-500 to-orange-600' 
            : 'bg-gradient-to-br from-cyan-500 to-blue-600'
        }`}>
          {preset.preset_type === 'master' ? (
            <Sliders className="w-8 h-8 text-white" />
          ) : (
            <Music className="w-8 h-8 text-white" />
          )}
        </div>
        
        {/* Preview Button */}
        <div className="absolute bottom-2 right-2">
          <PresetPreviewButton 
            presetId={preset.id} 
            size="icon"
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          />
        </div>
        
        {/* Price badge */}
        <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold ${
          isFree 
            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
            : 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
        }`}>
          {isFree ? 'FREE' : `$${price.toFixed(2)}`}
        </div>
        
        {/* Type badge */}
        <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium ${
          preset.preset_type === 'master'
            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
            : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
        }`}>
          {preset.preset_type === 'master' ? 'Master' : 'Track'}
        </div>
        
        {/* Featured badge */}
        {preset.is_featured && (
          <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-500/20 text-violet-400 border border-violet-500/30 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            Featured
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-white truncate mb-1" title={preset.name}>
          {preset.name}
        </h3>
        <p className="text-sm text-zinc-400 line-clamp-2 h-10 mb-3">
          {preset.description || 'No description'}
        </p>
        
        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3 h-6 overflow-hidden">
          {preset.genre && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-violet-500/10 text-violet-400">
              {preset.genre}
            </span>
          )}
          {preset.instrument_type && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-cyan-500/10 text-cyan-400">
              {preset.instrument_type}
            </span>
          )}
          {preset.style && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-rose-500/10 text-rose-400">
              {preset.style}
            </span>
          )}
        </div>
        
        {/* Author */}
        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
          <User className="w-3 h-3" />
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `/marketplace/user/${preset.author_id}`;
            }}
            className="hover:text-violet-400 hover:underline transition-colors"
            data-testid={`author-link-${preset.author_id}`}
          >
            {preset.author_name}
          </button>
          <span className="ml-auto">{preset.plugin_count} plugins</span>
        </div>
        
        {/* Stats */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Rating */}
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              <span className="text-sm text-white">{preset.average_rating?.toFixed(1) || '0.0'}</span>
              <span className="text-xs text-zinc-500">({preset.rating_count || 0})</span>
            </div>
            
            {/* Upvotes */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpvote(preset.id);
              }}
              className="flex items-center gap-1 text-zinc-400 hover:text-emerald-400 transition-colors"
            >
              <ArrowUp className="w-4 h-4" />
              <span className="text-sm">{preset.upvote_count || 0}</span>
            </button>
          </div>
          
          {/* Downloads */}
          <div className="flex items-center gap-1 text-zinc-500">
            <Download className="w-4 h-4" />
            <span className="text-sm">{preset.download_count || 0}</span>
          </div>
        </div>
      </div>
      
      {/* Hover Actions */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-zinc-900 via-zinc-900/95 to-transparent"
          >
            <Button
              onClick={handlePurchase}
              disabled={isPurchasing}
              className={`w-full ${
                isFree || isOwner || isPurchased
                  ? 'bg-violet-600 hover:bg-violet-500'
                  : 'bg-emerald-600 hover:bg-emerald-500'
              } text-white`}
              data-testid={`${isFree || isPurchased ? 'download' : 'buy'}-preset-${preset.id}`}
            >
              {isPurchasing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Processing...
                </>
              ) : isFree || isOwner || isPurchased ? (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download Preset
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Buy for ${price.toFixed(2)}
                </>
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Filter Panel Component
const FilterPanel = ({ 
  isOpen, 
  onClose, 
  categories, 
  filters, 
  setFilters 
}) => {
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="w-64 flex-shrink-0 bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 h-fit"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Filter className="w-4 h-4" />
          Filters
        </h3>
        <button onClick={onClose} className="text-zinc-400 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Preset Type */}
      <div className="mb-4">
        <label className="text-sm text-zinc-400 mb-2 block">Type</label>
        <div className="flex gap-2">
          <button
            onClick={() => setFilters(f => ({ ...f, preset_type: null }))}
            className={`flex-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              !filters.preset_type 
                ? 'bg-violet-600 text-white' 
                : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilters(f => ({ ...f, preset_type: 'track' }))}
            className={`flex-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              filters.preset_type === 'track' 
                ? 'bg-cyan-600 text-white' 
                : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}
          >
            Track
          </button>
          <button
            onClick={() => setFilters(f => ({ ...f, preset_type: 'master' }))}
            className={`flex-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              filters.preset_type === 'master' 
                ? 'bg-amber-600 text-white' 
                : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}
          >
            Master
          </button>
        </div>
      </div>
      
      {/* Genre */}
      <div className="mb-4">
        <label className="text-sm text-zinc-400 mb-2 block">Genre</label>
        <select
          value={filters.genre || ''}
          onChange={(e) => setFilters(f => ({ ...f, genre: e.target.value || null }))}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500"
        >
          <option value="">All Genres</option>
          {categories.genres?.map(genre => (
            <option key={genre} value={genre}>{genre}</option>
          ))}
        </select>
      </div>
      
      {/* Instrument Type */}
      <div className="mb-4">
        <label className="text-sm text-zinc-400 mb-2 block">Instrument</label>
        <select
          value={filters.instrument_type || ''}
          onChange={(e) => setFilters(f => ({ ...f, instrument_type: e.target.value || null }))}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500"
        >
          <option value="">All Instruments</option>
          {categories.instrument_types?.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>
      
      {/* Style */}
      <div className="mb-4">
        <label className="text-sm text-zinc-400 mb-2 block">Style</label>
        <select
          value={filters.style || ''}
          onChange={(e) => setFilters(f => ({ ...f, style: e.target.value || null }))}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500"
        >
          <option value="">All Styles</option>
          {categories.styles?.map(style => (
            <option key={style} value={style}>{style}</option>
          ))}
        </select>
      </div>
      
      {/* Clear Filters */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setFilters({ preset_type: null, genre: null, instrument_type: null, style: null })}
        className="w-full text-zinc-400 hover:text-white"
      >
        Clear All Filters
      </Button>
    </motion.div>
  );
};

// Main Marketplace Page
export const MarketplacePage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  // State
  const [presets, setPresets] = useState([]);
  const [featuredPresets, setFeaturedPresets] = useState([]);
  const [categories, setCategories] = useState({ genres: [], instrument_types: [], styles: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    preset_type: null,
    genre: null,
    instrument_type: null,
    style: null
  });
  const [sortBy, setSortBy] = useState('popular');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [activeTab, setActiveTab] = useState('presets'); // 'presets', 'songs', 'gear'
  
  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/marketplace/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);
  
  // Fetch featured presets
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await fetch(`${API_URL}/api/marketplace/presets/featured?limit=5`);
        if (response.ok) {
          const data = await response.json();
          // Filter out test presets from featured
          const filteredFeatured = (data.featured || []).filter(preset => {
            const name = preset.name || '';
            return !name.startsWith('TEST') && !name.startsWith('Test Preset');
          });
          setFeaturedPresets(filteredFeatured);
        }
      } catch (error) {
        console.error('Failed to fetch featured:', error);
      }
    };
    fetchFeatured();
  }, []);
  
  // Fetch presets
  const fetchPresets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filters.preset_type) params.append('preset_type', filters.preset_type);
      if (filters.genre) params.append('genre', filters.genre);
      if (filters.instrument_type) params.append('instrument_type', filters.instrument_type);
      if (filters.style) params.append('style', filters.style);
      params.append('sort_by', sortBy);
      params.append('page', page.toString());
      params.append('limit', '20');
      
      const response = await fetch(`${API_URL}/api/marketplace/presets?${params}`);
      if (response.ok) {
        const data = await response.json();
        // Filter out test presets in production (names starting with TEST or Test)
        const filteredPresets = (data.presets || []).filter(preset => {
          const name = preset.name || '';
          return !name.startsWith('TEST') && !name.startsWith('Test Preset');
        });
        setPresets(filteredPresets);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch presets:', error);
      toast.error('Failed to load presets');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters, sortBy, page]);
  
  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);
  
  // Handle download
  const handleDownload = async (presetId) => {
    if (!user) {
      toast.error('Please log in to download presets');
      navigate('/');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/marketplace/presets/${presetId}/download?user_id=${user.id}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Store the preset in localStorage for import into DAW
        localStorage.setItem('importedPreset', JSON.stringify(data));
        toast.success('Preset downloaded! Open it in the studio to apply.');
        
        // Navigate to studio
        navigate('/studio');
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download preset');
    }
  };
  
  // Handle upvote
  const handleUpvote = async (presetId) => {
    if (!user) {
      toast.error('Please log in to upvote');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/marketplace/presets/${presetId}/upvote?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Update local state
        setPresets(prev => prev.map(p => 
          p.id === presetId ? { ...p, upvote_count: data.upvote_count } : p
        ));
        toast.success('Vote recorded!');
      }
    } catch (error) {
      console.error('Upvote error:', error);
    }
  };
  
  // Handle view details
  const handleViewDetails = (preset) => {
    navigate(`/marketplace/${preset.id}`);
  };
  
  // Handle purchase
  const handlePurchase = async (presetId) => {
    if (!user) {
      toast.error('Please log in to purchase presets');
      navigate('/');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/payments/preset/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preset_id: presetId,
          origin_url: window.location.origin
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Purchase failed');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.message || 'Failed to initiate purchase');
    }
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="bg-zinc-900/50 border-b border-zinc-800 sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Logo & Title */}
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/projects')}
                className="text-zinc-400 hover:text-white"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-500" />
                  Marketplace
                </h1>
                <p className="text-sm text-zinc-400">Presets, songs, gear & instruments</p>
              </div>
            </div>
            
            {/* Search */}
            <div className="flex-1 max-w-xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  placeholder="Search presets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-zinc-800/50 border-zinc-700 focus:border-violet-500"
                  data-testid="marketplace-search"
                />
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={`${showFilters ? 'text-violet-400' : 'text-zinc-400'} hover:text-white`}
              >
                <Filter className="w-5 h-5" />
              </Button>
              
              {user && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/marketplace/dashboard')}
                    className="border-zinc-700 text-zinc-400 hover:text-white hover:border-emerald-500"
                    data-testid="creator-dashboard-btn"
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Earnings
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/marketplace/manage')}
                    className="border-zinc-700 text-zinc-400 hover:text-white hover:border-cyan-500"
                    data-testid="manage-presets-btn"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    My Presets
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/marketplace/user/${user.id}`)}
                    className="border-zinc-700 text-zinc-400 hover:text-white hover:border-violet-500"
                    data-testid="my-profile-btn"
                  >
                    <User className="w-4 h-4 mr-2" />
                    My Profile
                  </Button>
                  <Button
                    onClick={() => navigate('/marketplace/upload')}
                    className="bg-violet-600 hover:bg-violet-500"
                    data-testid="upload-preset-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Share Preset
                  </Button>
                  {/* Admin button - only visible to admin users */}
                  {ADMIN_EMAILS.includes(user.email?.toLowerCase()) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/admin-dashboard')}
                      className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20 hover:border-amber-400"
                      data-testid="admin-panel-btn"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin
                    </Button>
                  )}
                  <NotificationCenter />
                </>
              )}
            </div>
          </div>
          
          {/* Sort & View Options */}
          <div className="flex items-center justify-between mt-4">
            {/* Marketplace Type Toggle */}
            <div className="flex items-center gap-4">
              <div className="flex gap-1 bg-zinc-800/50 rounded-lg p-1 mr-4">
                <button
                  onClick={() => setActiveTab('presets')}
                  className={`px-4 py-1.5 rounded-md text-sm ${activeTab === 'presets' ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:text-white transition-colors'}`}
                  data-testid="presets-tab-btn"
                >
                  <Sliders className="w-4 h-4 inline mr-1.5" />
                  Presets
                </button>
                <button
                  onClick={() => setActiveTab('songs')}
                  className={`px-4 py-1.5 rounded-md text-sm ${activeTab === 'songs' ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:text-white transition-colors'}`}
                  data-testid="songs-tab-btn"
                >
                  <Headphones className="w-4 h-4 inline mr-1.5" />
                  Songs
                </button>
                <button
                  onClick={() => setActiveTab('gear')}
                  className={`px-4 py-1.5 rounded-md text-sm ${activeTab === 'gear' ? 'bg-orange-500 text-white' : 'text-zinc-400 hover:text-white transition-colors'}`}
                  data-testid="gear-tab-btn"
                >
                  <Guitar className="w-4 h-4 inline mr-1.5" />
                  Gear & Instruments
                </button>
              </div>
              
              {activeTab === 'presets' && (
              <>
              <span className="text-sm text-zinc-400">Sort by:</span>
              <div className="flex gap-1 bg-zinc-800/50 rounded-lg p-1">
                <button
                  onClick={() => setSortBy('popular')}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    sortBy === 'popular' ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <TrendingUp className="w-4 h-4 inline mr-1" />
                  Popular
                </button>
                <button
                  onClick={() => setSortBy('newest')}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    sortBy === 'newest' ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <Clock className="w-4 h-4 inline mr-1" />
                  Newest
                </button>
                <button
                  onClick={() => setSortBy('top_rated')}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    sortBy === 'top_rated' ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <Star className="w-4 h-4 inline mr-1" />
                  Top Rated
                </button>
              </div>
              </>
              )}
            </div>
            
            {activeTab === 'presets' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-zinc-800 text-white' : 'text-zinc-400'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-zinc-800 text-white' : 'text-zinc-400'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
            )}
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Gear Tab - Reverb Integration */}
        {activeTab === 'gear' ? (
          <Suspense fallback={
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          }>
            <ReverbGearBrowser />
          </Suspense>
        ) : activeTab === 'songs' ? (
          <div className="text-center py-16">
            <Headphones className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400 font-medium mb-2">Song Marketplace</p>
            <Button variant="outline" onClick={() => navigate('/songs')} className="border-zinc-700 text-zinc-300">
              Go to Song Marketplace
            </Button>
          </div>
        ) : (
        <div className="flex gap-6">
          {/* Filter Panel */}
          <AnimatePresence>
            {showFilters && (
              <FilterPanel
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                categories={categories}
                filters={filters}
                setFilters={setFilters}
              />
            )}
          </AnimatePresence>
          
          {/* Presets Grid */}
          <div className="flex-1">
            {/* Featured Section */}
            {featuredPresets.length > 0 && !searchQuery && page === 1 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-500" />
                  Featured Presets
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {featuredPresets.slice(0, 3).map(preset => (
                    <PresetCard
                      key={preset.id}
                      preset={preset}
                      onDownload={handleDownload}
                      onUpvote={handleUpvote}
                      onViewDetails={handleViewDetails}
                      onPurchase={handlePurchase}
                      currentUserId={user?.id}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* All Presets */}
            <div>
              <h2 className="text-lg font-semibold mb-4">
                {searchQuery ? `Results for "${searchQuery}"` : 'All Presets'}
              </h2>
              
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {[...Array(8)].map((_, i) => (
                    <div key={`skeleton-${i}`} className="bg-zinc-900/50 border border-zinc-800 rounded-xl h-72 animate-pulse" />
                  ))}
                </div>
              ) : presets.length === 0 ? (
                <div className="text-center py-20">
                  <Music className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-zinc-400 mb-2">No presets found</h3>
                  <p className="text-zinc-500 mb-4">
                    {searchQuery ? 'Try different search terms' : 'Be the first to share a preset!'}
                  </p>
                  {user && (
                    <Button onClick={() => navigate('/marketplace/upload')} className="bg-violet-600 hover:bg-violet-500">
                      <Plus className="w-4 h-4 mr-2" />
                      Share Your First Preset
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className={`grid gap-4 ${
                    viewMode === 'grid' 
                      ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                      : 'grid-cols-1'
                  }`}>
                    {presets.map(preset => (
                      <PresetCard
                        key={preset.id}
                        preset={preset}
                        onDownload={handleDownload}
                        onUpvote={handleUpvote}
                        onViewDetails={handleViewDetails}
                        onPurchase={handlePurchase}
                        currentUserId={user?.id}
                      />
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-8 pb-16">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="text-zinc-400"
                      >
                        <ChevronLeft className="w-5 h-5" />
                        Previous
                      </Button>
                      <span className="text-sm text-zinc-400">
                        Page {page} of {totalPages}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="text-zinc-400"
                      >
                        Next
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        )}
      </main>
    </div>
  );
};

export default MarketplacePage;
